import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// _mock
import { _addressBooks, INVOICE_SERVICE_OPTIONS } from 'src/_mock';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import FormProvider, { RHFSelect } from 'src/components/hook-form';
//
import CreateEditLabor from './create-edit-labor';
import { DatePicker } from '@mui/x-date-pickers';
import { Box, Button, DialogActions, Divider, MenuItem } from '@mui/material';
import { useData } from 'src/contexts/gantt-chart/gantt-context';
import { dateformation2 } from 'src/components/date-format-mdy';
import { postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/components/risk-management/risk-register/feching';
import { useSnackbar } from 'notistack';
// ----------------------------------------------------------------------

export default function Labors({ gantt, selectedDate, close }) {
  const router = useRouter();
  const [warning, setWarning] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const loadingSave = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const loadingSend = useBoolean();
  const { dateWiseAssignData, refreshAssignData, getDateWiseAssigment } = useData();

  const NewInvoiceSchema = Yup.object().shape({});

  const defaultValues = useMemo(
    () => ({
      start_date: selectedDate,
      activity: '',
      items: [
        // {
        //   title: '',
        //   noOfPeople: '',
        //   noOfShifts: '',
        //   noOfDays: '',
        //   requiredHours: '',
        //   totalHours: '',
        //   skillLevel: '',
        //   rate: '',
        //   amount: 0,
        //   name: [],
        // },
      ],
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(NewInvoiceSchema),
    defaultValues,
  });

  const {
    reset,
    control,
    setValue,
    setError,
    clearErrors,
    getValues,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleSaveAsDraft = handleSubmit(async (data) => {});

  const handleCreateAndSend = handleSubmit(async (data) => {
    setLoading(true);
    const assigned_labor = [];
    data.items.map((item) => {
      const employeeId = item.name?.map((emp) => ({ id: emp.id }));
      assigned_labor.push({
        trade_worker_title_id: item.title,
        no_of_people: item.noOfPeople,
        no_of_shifts: item.noOfShifts,
        no_of_days: item.noOfDays,
        required_hours: item.requiredHours,
        skill_level: item.skillLevel,
        cost_rate: item.rate,
        employees: employeeId,
      });
    });
    const dataForApi = {
      start_date: dateformation2(selectedDate),
      assignable_id: data.activity,
      assignable_type: 'App\\PlannedCostCode',
      assigned_labor,
    };

    try {
      const response = await postRequest(endpoints.ganttChart.assign_labours, dataForApi, false);
      console.log(response);
      if (response.metadata && response.metadata.success) {
        const refreshData = await refreshAssignData();
        gantt.parse(refreshData);
        close();
        await getDateWiseAssigment(dateformation2(selectedDate));
        reset();
        enqueueSnackbar({
          variant: 'success',
          message: 'Labour has been assigned successfully.',
        });
        return;
      }
      if (!(response.metadata && response.metadata.success)) {
        throw new Error(response?.message || 'Failed to assign');
      }
    } catch (err) {
      console.error('Error:', err);
      // setResourseData(JSON.parse(JSON.stringify(backupData))); // Restore deep copy
      handleErrorMessages(err.message || 'Submission failed.');
      return err.message;
    } finally {
      setLoading(false);
    }
  });

  return (
    <FormProvider methods={methods}>
      <Box
        gap={3}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(3, 1fr)',
          lg: 'repeat(3, 1fr)',
        }}
      >
        <Controller
          name="start_date"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DatePicker
              disabled
              label="Start Date"
              value={field.value}
              onChange={(newValue) => {
                field.onChange(newValue);
                const endDate = methods.getValues('end_date');
                if (newValue && endDate && newValue > endDate) {
                  setError('start_date', {
                    message: 'Start Date cannot be later than End Date',
                  });
                } else {
                  clearErrors('start_date');
                  clearErrors('end_date');
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !!error || !!warning,
                  helperText: error?.message || warning,
                },
              }}
            />
          )}
        />
        <RHFSelect
          onChange={(e) => {
            setValue('activity', e.target.value);
            const selectedActivity = dateWiseAssignData?.activities?.find(
              (item) => item.id == e.target.value
            );
            setSelectedActivity(selectedActivity);
            if (selectedActivity.assigned_labor && selectedActivity.assigned_labor.length > 0) {
              let arr = [];
              selectedActivity.assigned_labor.map((subItem) => {
                const selectedEmployeeIds = subItem.employees.map((employ) => ({
                  id: employ.employee_id,
                  first_name: employ.first_name,
                  last_name: employ.last_name,
                }));
                arr.push({
                  title: subItem?.trade_worker_title_id,
                  noOfPeople: subItem?.no_of_people,
                  noOfShifts: subItem?.no_of_shifts,
                  noOfDays: subItem?.no_of_days,
                  requiredHours: (selectedActivity.labor_hours / selectedActivity.duration).toFixed(
                    2
                  ),
                  totalHours: '',
                  skillLevel: subItem.skill_level,
                  rate: subItem.cost_rate,
                  amount: 0,
                  name: selectedEmployeeIds,
                });
              });

              setValue('items', arr);
            } else {
              setValue('items', [
                {
                  title: '',
                  noOfPeople: '',
                  noOfShifts: '',
                  noOfDays: '',
                  requiredHours: (selectedActivity.labor_hours / selectedActivity.duration).toFixed(
                    2
                  ),
                  totalHours: '',
                  skillLevel: '',
                  rate: '',
                  amount: 0,
                  name: [],
                },
              ]);
            }
          }}
          size="small"
          name="activity"
          label="Activity"
        >
          {dateWiseAssignData?.activities?.length > 0 ? (
            dateWiseAssignData?.activities?.map((service) => (
              <MenuItem key={service.id} value={service.id}>
                {service.description}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No Activity</MenuItem>
          )}
        </RHFSelect>
      </Box>
      <Divider sx={{ marginTop: '20px' }} />
      <CreateEditLabor selectedActivity={selectedActivity} />
      <DialogActions sx={{ paddingRight: '0px' }}>
        <Button
          variant="outlined"
          onClick={() => {
            close();
          }}
          className="danager"
        >
          Cancel
        </Button>
        <LoadingButton loading={loading} variant="contained" onClick={handleCreateAndSend}>
          Submit
        </LoadingButton>
      </DialogActions>
    </FormProvider>
  );
}

Labors.propTypes = {
  currentInvoice: PropTypes.object,
};
